/**
 * Smooth, car-like lane changes. The lateral position follows a critically damped spring towards the
 * target lane (eases in, glides, settles without overshoot), and sideways speed is limited by forward
 * speed through a maximum steering angle, so a slow vehicle can only creep over and a stopped one can't slide.
 */
export interface Lateral {
  x: number;
  /** Sideways speed, m/s. */
  vx: number;
}

export interface SteerProfile {
  /** Spring stiffness: higher = snappier lane changes. */
  stiffness: number;
  /** Largest sideways acceleration, m/s²: this is what makes the turn-in gentle rather than a jolt. */
  maxAccel: number;
  /** Largest steering angle, radians: limits sideways speed at a given forward speed. */
  maxYaw: number;
  /** Hard cap on sideways speed, m/s. */
  maxLateral: number;
  /** Skip the spring: slide straight to the lane at full sideways speed (the sharp phone flick). */
  direct?: boolean;
}

/** Desktop and traffic: a smooth, car-like glide over about a second. */
export const STEER: SteerProfile = { stiffness: 30, maxAccel: 10, maxYaw: 0.3, maxLateral: 7 };

/** Phones: a sharp flick, one lane (3.4 m) in about a tenth of a second at speed, so a swipe feels instant. */
export const STEER_SHARP: SteerProfile = { stiffness: 0, maxAccel: Infinity, maxYaw: 1.2, maxLateral: 32, direct: true };

/**
 * Less grip (rain) makes every lane change slower and softer: lower acceleration and sideways speed,
 * and a lazier spring. At grip 1 the profile is unchanged.
 */
export function withGrip(profile: SteerProfile, grip: number): SteerProfile {
  if (grip >= 1) return profile;
  return { ...profile, stiffness: profile.stiffness * grip, maxAccel: profile.maxAccel * grip, maxYaw: profile.maxYaw * grip, maxLateral: profile.maxLateral * Math.sqrt(grip) };
}

/** How far the body turns on screen, whatever the profile, so a sharp flick doesn't spin the bus. */
export const MAX_VISUAL_YAW = 0.3;

const SETTLE_X = 0.03;
const SETTLE_V = 0.25;

/** Moves `lateral` towards `goal` for one tick. Returns the sideways acceleration (for body roll). */
export function steerToward(lateral: Lateral, goal: number, forwardSpeed: number, dt: number, profile: SteerProfile = STEER): number {
  // The menu ticks with dt = 0; nothing moves, and dividing by it below would give NaN.
  if (dt <= 0) return 0;
  if (profile.direct) return slideToward(lateral, goal, forwardSpeed, dt, profile);
  const damping = 2 * Math.sqrt(profile.stiffness);
  const spring = profile.stiffness * (goal - lateral.x) - damping * lateral.vx;
  const accel = Math.max(-profile.maxAccel, Math.min(profile.maxAccel, spring));
  const maxVx = Math.min(profile.maxLateral, Math.max(0, forwardSpeed) * Math.tan(profile.maxYaw));
  const before = lateral.vx;
  lateral.vx = Math.max(-maxVx, Math.min(maxVx, lateral.vx + accel * dt));
  lateral.x += lateral.vx * dt;
  if (Math.abs(goal - lateral.x) < SETTLE_X && Math.abs(lateral.vx) < SETTLE_V) {
    lateral.x = goal;
    lateral.vx = 0;
  }
  return (lateral.vx - before) / dt;
}

/** Straight, constant-speed slide to the goal; stops exactly on it. Still can't move sideways when stopped. */
function slideToward(lateral: Lateral, goal: number, forwardSpeed: number, dt: number, profile: SteerProfile): number {
  const before = lateral.vx;
  const maxVx = Math.min(profile.maxLateral, Math.max(0, forwardSpeed) * Math.tan(profile.maxYaw));
  const gap = goal - lateral.x;
  const step = Math.min(Math.abs(gap), maxVx * dt);
  lateral.x = Math.abs(gap) <= step ? goal : lateral.x + Math.sign(gap) * step;
  lateral.vx = lateral.x === goal ? 0 : Math.sign(gap) * maxVx;
  return (lateral.vx - before) / dt;
}

/** Heading angle from sideways and forward speed (0 = straight down the road). */
export function headingOf(vx: number, forwardSpeed: number): number {
  const yaw = Math.atan2(vx, Math.max(0.5, forwardSpeed));
  return Math.max(-MAX_VISUAL_YAW, Math.min(MAX_VISUAL_YAW, yaw));
}
